import { 
  Box, 
  Card, 
  CardContent,
  Chip,
  Container,
  Grid,
  Link,
  Paper,
  Typography,
  Button,
  useTheme,
  alpha,
  Avatar,
  Divider,
  Stack
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SearchIcon from '@mui/icons-material/Search';
import ArticleIcon from '@mui/icons-material/Article';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import StarIcon from '@mui/icons-material/Star';
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../common/layouts/dashboard";
import { useAppDispatch } from "../../core/hooks/useStore";
import { RootState } from "../../core/store/store";
import { homeSlice } from "../home";
import NichePigBasicImg from "./niche-pig-basic.png";
import NichePigCardImg from "./niche-pig-card.png";

interface ToolItem {
  name: string;
  path: string;
  key: string;
  desc: string;
  img: any;
  icon?: React.ReactNode;
  features?: string[];
  category: 'pro' | 'free';
  badge?: string;
  color?: string;
}

const Tools = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const authData = useSelector((state: RootState) => state.auth);
  
  useEffect(() => {
    dispatch(homeSlice.actions.setActiveMenu("tools"));
  }, [dispatch]);

  useEffect(() => {
    document.title = "Wordform AI - Tools";
  }, []);

  const allToolKeys = authData.tools ? Object.keys(authData.tools) : [];
  const activeTools = allToolKeys.filter(
    (key) => authData.tools && authData.tools[key] !== null
  );

  function isToolPurchased(toolKey: string): boolean {
    const hasCredits = authData.tools && authData.tools[toolKey] > 0;
    const isUnlimited = authData.tools && authData.tools[toolKey] === "unlimited";
    return activeTools.includes(toolKey) && (!!hasCredits || !!isUnlimited);
  }

  const tools: ToolItem[] = [
    {
      name: "Niche Pig Pro",
      path: "/tools/niche-tool",
      key: "nicheTool",
      desc: "Discover profitable niches with AI-powered research. Simply enter an interest and let Niche Pig do the heavy lifting.",
      img: NichePigCardImg,
      icon: <SearchIcon />,
      features: [
        "AI-powered niche analysis",
        "Profitability scores",
        "Competition analysis",
        "Trend predictions"
      ],
      category: 'pro',
      color: theme.palette.primary.main
    },
    {
      name: "Headline Pig Pro",
      path: "/tools/headline-tool",
      key: "headlineTool",
      desc: "Generate high-converting headlines that capture attention and drive clicks. Create an entire content calendar instantly.",
      img: NichePigCardImg,
      icon: <ArticleIcon />,
      features: [
        "Buyer-intent headlines",
        "Content calendar creation",
        "Click-through optimization",
        "A/B testing suggestions"
      ],
      category: 'pro',
      color: theme.palette.secondary.main
    },
    {
      name: "Keyword Pig Pro",
      path: "/tools/keyword-tool",
      key: "keywordTool",
      desc: "Find buyer-intent keywords in seconds. Get comprehensive keyword lists with search volume and competition data.",
      img: NichePigCardImg,
      icon: <TrendingUpIcon />,
      features: [
        "Buyer-intent keywords",
        "Long-tail suggestions",
        "Search volume data",
        "SEO difficulty scores"
      ],
      category: 'pro',
      color: theme.palette.primary.main
    },
    {
      name: "Niche Pig",
      path: "https://wordform.ai/tools/niche-generator/",
      key: "nicheToolBasic",
      desc: "The original niche research tool. Perfect for beginners looking to explore profitable niches.",
      img: NichePigBasicImg,
      icon: <LightbulbIcon />,
      features: [
        "Basic niche research",
        "Quick results",
        "Beginner friendly"
      ],
      category: 'free',
      color: theme.palette.info.main
    },
    {
      name: "Keyword Pig",
      path: "https://wordform.ai/tools/keyword-generator/",
      key: "keywordToolBasic",
      desc: "Essential keyword research for content creators. Find relevant keywords for your niche.",
      img: NichePigBasicImg,
      icon: <SearchIcon />,
      features: [
        "Keyword suggestions",
        "Topic ideas",
        "Quick generation"
      ],
      category: 'free',
      color: theme.palette.info.main
    },
    {
      name: "Headline Pig",
      path: "https://wordform.ai/tools/headline-generator/",
      key: "headlineToolBasic",
      desc: "Create compelling headlines that grab attention. Perfect for blog posts and articles.",
      img: NichePigBasicImg,
      icon: <AutoFixHighIcon />,
      features: [
        "Headline variations",
        "Emotional triggers",
        "SEO optimization"
      ],
      category: 'free',
      color: theme.palette.info.main
    }
  ];

  const proTools = tools.filter(tool => tool.category === 'pro');
  const freeTools = tools.filter(tool => tool.category === 'free');

  const ToolCard = ({ tool }: { tool: ToolItem }) => {
    const isPurchased = tool.category === 'free' || isToolPurchased(tool.key);
    const isExternal = tool.path.startsWith('http');
    
    // Get the credit status for the tool
    const getToolStatus = () => {
      if (tool.category === 'free') {
        return { label: 'Free', color: 'success' };
      }
      
      const credits = authData.tools?.[tool.key];
      if (!credits || credits === null) {
        return { label: 'Not Available', color: 'default' };
      }
      if (credits === 'unlimited') {
        return { label: 'Unlimited', color: 'success' };
      }
      return { label: `${credits} Credits`, color: 'primary' };
    };
    
    const toolStatus = getToolStatus();

    return (
      <Grid item xs={12} md={6} lg={4}>
        <Card 
          sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            transition: 'all 0.3s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.shadows[8],
              borderColor: theme.palette.primary.main
            }
          }}
        >
          
          <Box
            sx={{
              height: 140,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderBottom: `1px solid ${theme.palette.divider}`
            }}
          >
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'white',
                border: `3px solid ${theme.palette.primary.main}`,
                color: theme.palette.primary.main
              }}
            >
              <Box sx={{ fontSize: 40 }}>
                {tool.icon}
              </Box>
            </Avatar>
          </Box>

          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
                {tool.name}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Chip
                  label={toolStatus.label}
                  size="small"
                  color={toolStatus.color as any}
                  variant="outlined"
                  sx={{
                    fontWeight: 500,
                    ...(toolStatus.color === 'success' && {
                      borderColor: theme.palette.success.main,
                      color: theme.palette.success.main,
                      bgcolor: alpha(theme.palette.success.main, 0.08)
                    }),
                    ...(toolStatus.color === 'primary' && {
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.08)
                    }),
                    ...(toolStatus.color === 'default' && {
                      borderColor: theme.palette.grey[400],
                      color: theme.palette.grey[600],
                      bgcolor: alpha(theme.palette.grey[500], 0.08)
                    })
                  }}
                />
              </Box>

              <Typography variant="body2" color="text.secondary" paragraph>
                {tool.desc}
              </Typography>
            </Box>

            <Box sx={{ mt: 'auto', pt: 2 }}>
              {isPurchased ? (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => {
                    if (isExternal) {
                      window.open(tool.path, '_blank');
                    } else {
                      navigate(tool.path);
                    }
                  }}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.9)
                    }
                  }}
                >
                  Open Tool
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="outlined"
                  disabled
                  startIcon={<LockIcon />}
                >
                  Upgrade to Access
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <DashboardLayout active={"tools"} name="Tools">
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 0 }}>
              AI Content Tools
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Powerful AI-driven tools to supercharge your content creation workflow
            </Typography>
          </Box>
        </Box>

        {/* Pro Tools Section */}
        <Box sx={{ mb: 6 }}>
          <Grid container spacing={3}>
            {proTools.map((tool, idx) => (
              <ToolCard key={idx} tool={tool} />
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Free Tools Section */}
        <Box>
          <Grid container spacing={3}>
            {freeTools.map((tool, idx) => (
              <ToolCard key={idx} tool={tool} />
            ))}
          </Grid>
        </Box>
      </Container>
    </DashboardLayout>
  );
};

export default Tools;
